import axios from 'axios';

const RAW = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
let BASE_URL = RAW;
if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
  BASE_URL = BASE_URL.replace(/^http:\/\//, 'https://');
}
// eslint-disable-next-line no-console
console.log('[TaskTool] API base:', BASE_URL, '| from env:', process.env.REACT_APP_API_URL);

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const url = original.url || '';
    // Auth endpoints (login/refresh) manage their own errors — don't treat their
    // 401 as an expired session, or a wrong-password login just bounces to /login.
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');
    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const r = await axios.post(`${BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
          localStorage.setItem('access_token', r.data.access_token);
          original.headers.Authorization = `Bearer ${r.data.access_token}`;
          return api(original);
        } catch {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username, password, captcha_token) => api.post('/auth/login', { username, password, captcha_token }),
  refresh: (token) => api.post('/auth/refresh', { refresh_token: token }),
};

export const usersAPI = {
  getMe: () => api.get('/users/me'),
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  deactivate: (id) => api.delete(`/users/${id}`),
  activate: (id) => api.post(`/users/${id}/activate`),
  changePassword: (data) => api.post('/users/me/change-password', data),
  resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
};

export const tasksAPI = {
  list: (params) => api.get('/tasks', { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  assign: (data) => api.post('/tasks/assign', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  hrUpdate: (id, data) => api.put(`/tasks/${id}/hr`, data),
  remove: (id) => api.delete(`/tasks/${id}`),
  timeLogs: (id) => api.get(`/tasks/${id}/time-logs`),
  addTimeLog: (id, data) => api.post(`/tasks/${id}/time-logs`, data),
  deleteTimeLog: (id, logId) => api.delete(`/tasks/${id}/time-logs/${logId}`),
};

export const documentsAPI = {
  list: (taskId) => api.get(`/documents/task/${taskId}`),
  recent: (params) => api.get('/documents/recent', { params }),
  upload: (taskId, formData) =>
    api.post(`/documents/upload/${taskId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  download: (docId) => api.get(`/documents/download/${docId}`, { responseType: 'blob' }),
  replace: (docId, formData) =>
    api.put(`/documents/${docId}/replace`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (docId) => api.delete(`/documents/${docId}`),
};

export const issuesAPI = {
  list: (params) => api.get('/issues', { params }),
  get: (id) => api.get(`/issues/${id}`),
  create: (data) => api.post('/issues', data),
  update: (id, data) => api.put(`/issues/${id}`, data),
};

export const concernsAPI = {
  list: (params) => api.get('/concerns', { params }),
  get: (id) => api.get(`/concerns/${id}`),
  create: (data) => api.post('/concerns', data),
  update: (id, data) => api.put(`/concerns/${id}`, data),
};

export const notificationsAPI = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

export const performanceAPI = {
  me: (params) => api.get('/performance/me', { params }),
  today: () => api.get('/performance/today'),
  employee: (id, params) => api.get(`/performance/employee/${id}`, { params }),
  overview: () => api.get('/performance/overview'),
  dailyHours: (userId, month, year) => api.get(`/performance/daily/${userId}`, { params: { month, year } }),
};

export const punchAPI = {
  punchIn: (data) => api.post('/punch/in', data || {}),
  punchOut: (data) => api.post('/punch/out', data || {}),
  today: () => api.get('/punch/today'),
  history: (params) => api.get('/punch/history', { params }),
};

export const leavesAPI = {
  list: (params) => api.get('/leaves', { params }),
  create: (data) => api.post('/leaves', data),
  update: (id, data) => api.put(`/leaves/${id}`, data),
};

export const taskRequestsAPI = {
  list: (params) => api.get('/task-requests', { params }),
  create: (data) => api.post('/task-requests', data),
  update: (id, data) => api.put(`/task-requests/${id}`, data),
};

export const feedbackAPI = {
  list: (params) => api.get('/feedback', { params }),
  create: (data) => api.post('/feedback', data),
  reply: (id, data) => api.put(`/feedback/${id}/reply`, data),
};

export const meetingsAPI = {
  list: () => api.get('/meetings'),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.put(`/meetings/${id}`, data),
};

export const onboardingAPI = {
  list: (params) => api.get('/onboarding', { params }),
  get: (id) => api.get(`/onboarding/${id}`),
  create: (data) => api.post('/onboarding', data),
  update: (id, data) => api.put(`/onboarding/${id}`, data),
  reassign: (id) => api.post(`/onboarding/${id}/reassign`),
  remove: (id) => api.delete(`/onboarding/${id}`),
};

export const chatAPI = {
  rooms: () => api.get('/chat/rooms'),
  users: (params) => api.get('/chat/users', { params }),
  createGroup: (data) => api.post('/chat/rooms', data),
  openDirect: (userId) => api.post(`/chat/rooms/direct/${userId}`),
  members: (roomId) => api.get(`/chat/rooms/${roomId}/members`),
  messages: (roomId, params) => api.get(`/chat/rooms/${roomId}/messages`, { params }),
  send: (roomId, content) => api.post(`/chat/rooms/${roomId}/messages`, { content }),
  sendAttachment: (roomId, formData) =>
    api.post(`/chat/rooms/${roomId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  fileUrl: (messageId) => `${BASE_URL}/chat/messages/${messageId}/file`,
  downloadFile: (messageId) => api.get(`/chat/messages/${messageId}/file`, { responseType: 'blob' }),
  deleteMessage: (messageId) => api.delete(`/chat/messages/${messageId}`),
};

export const expensesAPI = {
  list: (params) => api.get('/expenses', { params }),
  create: (data) => api.post('/expenses', data),
  review: (id, data) => api.put(`/expenses/${id}`, data),
  reimburseMonth: (params) => api.post('/expenses/reimburse-month', null, { params }),
  uploadReceipt: (id, formData) =>
    api.post(`/expenses/${id}/receipt`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadReceipt: (id) => api.get(`/expenses/${id}/receipt`, { responseType: 'blob' }),
  remove: (id) => api.delete(`/expenses/${id}`),
};

export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
  actions: () => api.get('/audit-logs/actions'),
};

export const adminMessagesAPI = {
  list: () => api.get('/admin-messages'),
  send: (content) => api.post('/admin-messages', { content }),
  reply: (id, reply) => api.put(`/admin-messages/${id}/reply`, { reply }),
};

export const reportsAPI = {
  downloadPerformance: (userId, params) =>
    api.get(`/reports/performance/${userId}/csv`, { params, responseType: 'blob' }),
  downloadMonthly: (userId, params) =>
    api.get(`/reports/monthly/${userId}/csv`, { params, responseType: 'blob' }),
  timesheet: (userId, params) => api.get(`/reports/timesheet/${userId}`, { params }),
};

export const vaultAPI = {
  status: () => api.get('/vault/status'),
  list: (params) => api.get('/vault/entries', { params }),
  categories: () => api.get('/vault/categories'),
  create: (data) => api.post('/vault/entries', data),
  update: (id, data) => api.put(`/vault/entries/${id}`, data),
  remove: (id) => api.delete(`/vault/entries/${id}`),
  // The only call that returns a plaintext password. Requires the caller to
  // re-enter their own login password and is logged server-side every time.
  reveal: (id, password) => api.post(`/vault/entries/${id}/reveal`, { password }),
  grants: (id) => api.get(`/vault/entries/${id}/grants`),
  grant: (id, data) => api.post(`/vault/entries/${id}/grants`, data),
  revoke: (grantId) => api.delete(`/vault/grants/${grantId}`),
  accessLog: (params) => api.get('/vault/access-log', { params }),
};

export const companyDocsAPI = {
  list: (params) => api.get('/company-documents', { params }),
  categories: () => api.get('/company-documents/categories'),
  // Entries are links, not uploads - plain JSON, no multipart.
  // Anyone may add; only a super admin may update/remove/share.
  add: (data) => api.post('/company-documents', data),
  update: (id, data) => api.put(`/company-documents/${id}`, data),
  remove: (id) => api.delete(`/company-documents/${id}`),
  shares: (id) => api.get(`/company-documents/${id}/grants`),
  share: (id, data) => api.post(`/company-documents/${id}/grants`, data),
  shareEveryone: (id) => api.post(`/company-documents/${id}/grants/everyone`),
  unshare: (grantId) => api.delete(`/company-documents/grants/${grantId}`),
};

export default api;
